import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ShieldCheck, Facebook, Instagram, Youtube, Truck, RotateCcw, Headphones, Send, Loader2, Check, MapPin, Phone } from "lucide-react";
import { navCategories } from "../mock";
import PartnerBanner from "./PartnerBanner";
import { PaymentLogos } from "./home/TrustBanner";
import { useSettings } from "../context/SettingsContext";
import api, { apiErrorMessage } from "../services/api";

const linkCls = "hover:text-white hover:pl-1 transition-all duration-200 inline-block";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setState("busy"); setMsg("");
    try { const { data } = await api.post("/newsletter", { email }); setState("done"); setMsg(data.message); setEmail(""); }
    catch (err) { setState("error"); setMsg(apiErrorMessage(err, "Inscription impossible.")); }
  };
  return (
    <div data-testid="footer-newsletter" className="relative rounded-3xl border border-[#222735] bg-gradient-to-br from-[#141721] to-[#0A0C10] p-7 md:p-10 overflow-hidden">
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#E01F1F]/20 blur-3xl pointer-events-none" />
      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B3B] font-bold mb-2">Newsletter</div>
          <h3 className="font-extrabold italic uppercase tracking-tight text-white text-2xl md:text-3xl leading-none">Promos, nouveautés & conseils d'entretien</h3>
          <p className="text-slate-400 text-[14px] mt-2">Une fois par mois, zéro spam. Désinscription en un clic.</p>
        </div>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 flex items-center bg-[#08090C] border border-[#2A3040] rounded-xl px-4 focus-within:border-[#E01F1F] transition-colors">
            <Mail size={16} className="text-slate-500" />
            <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.fr" className="flex-1 min-w-0 py-3.5 px-3 bg-transparent outline-none text-slate-50 placeholder:text-slate-500" />
          </div>
          <button data-testid="newsletter-submit" type="submit" disabled={state === "busy"} className="btn-orange btn-shine rounded-xl px-6 py-3.5 font-bold uppercase tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98]">
            {state === "busy" ? <Loader2 size={18} className="animate-spin" /> : state === "done" ? <Check size={18} /> : <Send size={18} />} S'inscrire
          </button>
          {msg && <div data-testid="newsletter-message" className={`sm:col-span-2 text-[13px] ${state === "error" ? "text-red-400" : "text-emerald-400"}`}>{msg}</div>}
        </form>
      </div>
    </div>
  );
};

const Footer = () => {
  const s = useSettings();
  const [categories, setCategories] = useState(navCategories);
  useEffect(() => { api.get("/categories").then(({ data }) => { if (Array.isArray(data) && data.length) setCategories(data); }).catch(() => {}); }, []);
  const reassurance = [
    { icon: Truck, t: `Livraison ${s.delivery_delay}`, d: "Expédition rapide, suivi en temps réel" },
    { icon: RotateCcw, t: `Retours ${s.return_days} jours`, d: "Satisfait ou remboursé" },
    { icon: ShieldCheck, t: "Pièces garanties", d: "Qualité d'origine ou équivalente" },
    { icon: Headphones, t: "Experts à votre écoute", d: "Conseil & aide au choix de la pièce" },
  ];
  return (
    <footer className="mt-16 relative z-10 pb-16 md:pb-0" data-testid="site-footer">
      <PartnerBanner />
      <div className="bg-[#040507] text-slate-400 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E01F1F] to-transparent" />
        <div className="max-w-[1440px] mx-auto px-5 pt-12">
          <Newsletter />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="footer-reassurance">
            {reassurance.map((r) => (
              <div key={r.t} className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-[#10131A] border border-[#222735] flex items-center justify-center"><r.icon size={18} className="text-[#FF3B3B]" /></div>
                <div><div className="text-white font-bold text-[14px] leading-tight">{r.t}</div><div className="text-[12px] text-slate-500 mt-0.5">{r.d}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-[1440px] mx-auto px-5 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-2">
            <Link to="/" className="inline-block mb-4"><img src="/logos/ktsauto-logo.png" alt="KTSauto" className="h-20 w-auto object-contain" /></Link>
            <p className="text-[14px] leading-relaxed mb-5 max-w-sm">Votre expert français de la pièce auto. Des milliers de références au meilleur prix, expédiées rapidement, 100 % compatibles avec votre véhicule.</p>
            <ul className="space-y-2 text-[14px]">
              <li className="flex items-center gap-2"><Mail size={15} className="text-[#FF3B3B]" /> contact@ktsauto.fr</li>
              {s.phone && <li className="flex items-center gap-2"><Phone size={15} className="text-[#FF3B3B]" /> {s.phone}</li>}
              {s.address && <li className="flex items-center gap-2"><MapPin size={15} className="text-[#FF3B3B]" /> {s.address}</li>}
            </ul>
            <div className="flex items-center gap-3 mt-5">
              {[[Facebook, "facebook"], [Instagram, "instagram"], [Youtube, "youtube"]].map(([Icon, n]) => (
                <a key={n} href="#" data-testid={`footer-social-${n}`} aria-label={n} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 text-slate-300 flex items-center justify-center hover:bg-[#E01F1F] hover:border-[#E01F1F] hover:text-white hover:-translate-y-0.5 transition-all"><Icon size={17} /></a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-white font-extrabold italic uppercase tracking-tight mb-4 text-[15px]">Catégories</h4>
            <ul className="space-y-2 text-[14px]">
              {categories.slice(0, 8).map((c) => <li key={c.slug}><Link to={`/categorie/${c.slug}`} className={linkCls}>{c.name}</Link></li>)}
              <li><Link to="/categorie/promotions" className={`${linkCls} text-[#FF3B3B] font-semibold`}>Promotions</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-extrabold italic uppercase tracking-tight mb-4 text-[15px]">Services</h4>
            <ul className="space-y-2 text-[14px]">
              <li><Link to="/vehicule" className={linkCls}>Recherche par véhicule</Link></li>
              <li><Link to="/pneumatiques" className={linkCls}>Pneumatiques</Link></li>
              <li><Link to="/performance" className={linkCls}>Performance</Link></li>
              <li><Link to="/carte-grise" className={linkCls}>Calcul carte grise</Link></li>
              <li><Link to="/comparer" className={linkCls}>Comparateur de pièces</Link></li>
              <li><Link to="/professionnels" data-testid="footer-pro" className={linkCls}>Espace Professionnels</Link></li>
              <li><Link to="/professionnels/conditions" data-testid="footer-pro-conditions" className={linkCls}>Conditions compte pro</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-extrabold italic uppercase tracking-tight mb-4 text-[15px]">Informations</h4>
            <ul className="space-y-2 text-[14px]">
              <li><Link to="/a-propos" data-testid="footer-about" className={linkCls}>À propos de nous</Link></li>
              <li><Link to="/livraison-retours" data-testid="footer-shipping" className={linkCls}>Livraison &amp; retours</Link></li>
              <li><Link to="/aide" data-testid="footer-help" className={linkCls}>Besoin d'aide&nbsp;?</Link></li>
              <li><Link to="/contact" data-testid="footer-contact" className={linkCls}>Nous contacter</Link></li>
              <li><Link to="/conditions-generales" data-testid="footer-terms" className={linkCls}>Conditions générales</Link></li>
              <li><Link to="/mentions-legales" data-testid="footer-legal" className={linkCls}>Mentions légales</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-[1440px] mx-auto px-5 py-5 flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-[12px] text-slate-500"><ShieldCheck size={15} className="text-[#FF3B3B]" /> Paiement sécurisé <PaymentLogos compact /></div>
            <span className="text-[13px] text-slate-500">© {new Date().getFullYear()} KTSauto — Tous droits réservés.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
